import { Box } from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { Address } from "@ryde/controls";
import { useGoogleServices } from "@ryde/redux";
import {
  appLanguageSelector,
  createErrorResult,
  errors,
  ICoordinate,
  IPlacePrediction,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { LayoutRight } from "../right";
import { AddressBlockMap } from "./address-block-map";

interface IAddressBlockRightProps {
  label?: string | null;
  value: IPlacePrediction | null;
  options: IPlacePrediction[];
  coordinate: ICoordinate | undefined;
  onSave: (value: IPlacePrediction, coordinate: ICoordinate) => void;
  onClose: () => void;
}

export const AddressBlockRight: FC<IAddressBlockRightProps> = ({
  label,
  value,
  options,
  coordinate,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const { getCoordinateByPlaceId, searchAddressByGeo } = useGoogleServices();
  const language = useRecoilValue(appLanguageSelector);
  const [tempValue, setTempValue] = useState<IPlacePrediction | null>(value);
  const [tempOptions, setTempOptions] = useState<IPlacePrediction[]>(options);
  const [tempCoordinate, setTempCoordinate] = useState<ICoordinate | undefined>(
    coordinate,
  );

  const handleSave = (): void => {
    if (tempValue && tempCoordinate) {
      onSave(tempValue, tempCoordinate);
    }
  };

  const handleTemporaryValueChange = async (
    newValue: IPlacePrediction | null,
  ): Promise<void> => {
    setTempValue(newValue);

    try {
      const coordinateResult = await getCoordinateByPlaceId(newValue?.placeId);

      if (!coordinateResult.success) {
        return warnAndToastSimpleResult(coordinateResult, t);
      }

      setTempCoordinate(coordinateResult.data);
    } catch (err) {
      warnAndToastSimpleResult(
        createErrorResult(
          errors.common.technicalProblemOccurred,
          "Error while changing address",
        ),
        t,
      );
    }
  };

  const handleLocationChange = async (location: ICoordinate): Promise<void> => {
    setTempCoordinate(location);

    try {
      const placeResult = await searchAddressByGeo({
        geoParams: {
          location,
          language,
        },
      });

      if (!placeResult.success) {
        return warnAndToastSimpleResult(placeResult, t);
      }

      setTempValue(placeResult.data ?? null);
    } catch (err) {
      warnAndToastSimpleResult(
        createErrorResult(
          errors.common.technicalProblemOccurred,
          "Error while changing location",
        ),
        t,
      );
    }
  };

  return (
    <LayoutRight
      showFooter
      closeClick={onClose}
      saveClick={handleSave}
      showBackgroundImage={false}
      positionFixed={true}
    >
      <Box sx={{ marginTop: "20px", padding: "0 24px", width: "100%" }}>
        <Address
          isLatLngMissing={!tempCoordinate}
          value={tempValue}
          defaultOptions={tempOptions}
          onChange={handleTemporaryValueChange}
          onOptionsChange={setTempOptions}
          label={label}
        />
      </Box>
      <AddressBlockMap
        coordinate={tempCoordinate}
        onLocationChange={handleLocationChange}
      />
    </LayoutRight>
  );
};
